import React from "react";
import { useNavigate } from "react-router-dom";
import { XpAmount } from "./XpAmount";
import { formatXpRank, type XpSummary } from "../../utils/xp";

/** XP hub modal and other solid surfaces. */
export const XP_STAT_BOX_BG = "bg-[#652DAF]";
/** Profile career stats boxes — #1B062F at 30% opacity. */
export const XP_STAT_BOX_BG_PROFILE = "bg-[#1B062F4D]";
/** XP hub modal — wider gaps between boxes. */
export const XP_STAT_BOX_GAP = "gap-2";
/** Profile career stats — tight 2px gaps. */
export const XP_STAT_BOX_GAP_PROFILE = "gap-[2px]";
export const XP_STAT_BOX_HEIGHT = "h-[50px]";
/** Profile career — weekly leaderboard finishes row. */
export const XP_WEEKLY_FINISHES_BOX_HEIGHT_PROFILE = "h-[70px]";

/** Inner horizontal padding inside stat / history tinted boxes (hub modal). */
export const XP_BOX_INNER_PX = "px-2";

export const XP_STAT_LABEL_CLASS_PROFILE =
  "text-center font-anton text-[10px] font-normal not-italic uppercase leading-[12px] tracking-[0%] text-white/40 [leading-trim:none]";

/** XP hub modal stat box labels. */
export const XP_STAT_LABEL_CLASS =
  "text-center font-anton text-[11px] font-normal not-italic uppercase leading-[12px] tracking-[0%] text-white/40 [leading-trim:none]";

export const XP_STAT_ICON_CLASS = "h-[17px] w-[17px] shrink-0 object-contain";

/** Stat values (XP amounts, week rank) — hub modal & profile. */
export const XP_STAT_VALUE_CLASS =
  "align-middle font-anton text-[15px] font-normal not-italic uppercase leading-[100%] tracking-[0%] text-white tabular-nums [leading-trim:none]";

function StatCell({
  label,
  children,
  cornerAction,
  boxBgClassName,
  labelClassName,
}: {
  label: string;
  children: React.ReactNode;
  cornerAction?: React.ReactNode;
  boxBgClassName: string;
  labelClassName: string;
}) {
  return (
    <div className={`relative flex ${XP_STAT_BOX_HEIGHT} flex-col items-center justify-center ${XP_BOX_INNER_PX} py-1 text-center ${boxBgClassName}`}>
      <p className={labelClassName}>{label}</p>
      <div className="mt-1 flex items-center justify-center">{children}</div>
      {cornerAction}
    </div>
  );
}

type XpCareerStatsRowProps = {
  xp: XpSummary | null;
  /** When set, week leaderboard arrow calls this instead of routing. */
  onLeaderboardClick?: () => void;
  /** Box fill; defaults to solid hub colour. */
  boxBgClassName?: string;
  /** Gap between boxes; defaults to hub spacing. */
  boxGapClassName?: string;
  /** Label typography; defaults to hub (11px Anton). */
  statLabelClassName?: string;
  /** XP icon size in amount cells; defaults to 17×17. */
  statIconClassName?: string;
  /** Value typography; defaults to 15px Anton uppercase. */
  statValueClassName?: string;
};

export function XpCareerStatsRow({
  xp,
  onLeaderboardClick,
  boxBgClassName = XP_STAT_BOX_BG,
  boxGapClassName = XP_STAT_BOX_GAP,
  statLabelClassName = XP_STAT_LABEL_CLASS,
  statIconClassName = XP_STAT_ICON_CLASS,
  statValueClassName = XP_STAT_VALUE_CLASS,
}: XpCareerStatsRowProps) {
  const navigate = useNavigate();
  const summary = xp ?? {
    total_xp: 0,
    week_xp: 0,
    week_rank: null,
    weekly_finishes: { first: 0, second: 0, third: 0 },
  };

  const openLeaderboard = () => {
    if (onLeaderboardClick) {
      onLeaderboardClick();
      return;
    }
    navigate("/xp-leaderboard");
  };

  return (
    <div className={`grid grid-cols-3 ${boxGapClassName} border-t border-white/5`}>
      <StatCell label="All time XP" boxBgClassName={boxBgClassName} labelClassName={statLabelClassName}>
        <XpAmount
          value={summary.total_xp}
          iconClassName={statIconClassName}
          valueClassName={statValueClassName}
          className="inline-flex items-center gap-1"
        />
      </StatCell>
      <StatCell label="XP This week" boxBgClassName={boxBgClassName} labelClassName={statLabelClassName}>
        <XpAmount
          value={summary.week_xp}
          iconClassName={statIconClassName}
          valueClassName={statValueClassName}
          className="inline-flex items-center gap-1"
        />
      </StatCell>
      <StatCell
        label="XP Weekly leaderboard"
        boxBgClassName={boxBgClassName}
        labelClassName={statLabelClassName}
        cornerAction={
          <button
            type="button"
            onClick={openLeaderboard}
            className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center bg-[#D2FF00] hover:brightness-95"
            aria-label="Open XP leaderboard"
          >
            <img src="/assets/arrow-right.svg" alt="" className="h-3 w-3 object-contain" aria-hidden />
          </button>
        }
      >
        <span className={statValueClassName}>{formatXpRank(summary.week_rank)}</span>
      </StatCell>
    </div>
  );
}
